import React, { ReactNode } from "react";
import { cn } from "../../lib/utils";

interface PageHeaderProps {
  title?: string;
  description?: string;
  icon?: React.ElementType;
  actions?: ReactNode;
  className?: string;
  variant?: "default" | "card";
  mobileActionsInHeader?: boolean;
}

/**
 * PageHeader component with two variants:
 * - "default": Full-width header  used for main pages without sub-sidebars.
 * - "card": Rounded card-style header with a purple left border, used for pages with the sub-sidebar like settings,websites,.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  icon: Icon,
  actions,
  className,
  variant = "default",
  mobileActionsInHeader = false,
}) => {
  const isCard = variant === "card";

  return (
    <div
      className={cn(
        "flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 transition-all duration-200",
        // Container styles based on variant
        isCard
          ? "bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-[#885ABB] p-3 sm:p-4 lg:p-6" // Responsive Card Padding
          : "bg-white border-b border-gray-200 px-3 py-2 sm:px-4 sm:py-3 lg:px-6 lg:py-4", // Responsive Full-width Padding
        className
      )}
    >
      <div className="flex items-center gap-2 lg:gap-4 min-w-0 w-full sm:w-auto">
        {Icon && (
          <div
            className={cn(
              "flex items-center justify-center rounded-xl transition-all duration-300 shrink-0",
              isCard ? "bg-purple-50 p-1.5 sm:p-2 lg:p-2.5" : "bg-[#F3E8FF] p-2 sm:p-2.5 lg:p-3"
            )}
          >
            <Icon
              className={cn(
                "text-[#885ABB]",
                isCard ? "h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-5 lg:w-5" : "h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6"
              )}
            />
          </div>
        )}
        <div className="flex flex-col justify-center min-w-0 flex-1">
          <div className="flex items-center justify-between sm:justify-start gap-2 min-w-0">
            {title && (
              <h1
                className={cn(
                  "text-gray-900 tracking-tight leading-tight truncate",
                  isCard ? "text-sm sm:text-base lg:text-xl font-bold" : "text-base sm:text-lg lg:text-2xl font-bold"
                )}
              >
                {title}
              </h1>
            )}
            {mobileActionsInHeader && actions && (
              <div className="sm:hidden shrink-0 flex items-center gap-1.5 [&_button]:text-[10px] [&_button]:h-7 [&_button]:px-2 [&_button]:gap-1 [&_svg]:h-3 [&_svg]:w-3">
                {actions}
              </div>
            )}
          </div>
          {description && (
            <p
              className={cn(
                "text-gray-500 mt-1 leading-relaxed hidden lg:block",
                isCard ? "text-[13px] font-medium" : "text-sm font-medium"
              )}
            >
              {description}
            </p>
          )}
        </div>
      </div>

      {actions && (
        <div className={cn(
          "flex items-center gap-1.5 sm:gap-2 lg:gap-3 shrink-0 w-full sm:w-auto sm:justify-end [&_button]:text-xs sm:[&_button]:text-sm [&_button]:h-8 sm:[&_button]:h-10 [&_button]:px-3 sm:[&_button]:px-4 [&_button]:gap-1 sm:[&_button]:gap-2 [&_svg]:h-3.5 [&_svg]:w-3.5 sm:[&_svg]:h-4 sm:[&_svg]:w-4",
          mobileActionsInHeader && "hidden sm:flex"
        )}>
          {actions}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
